import { useState, useEffect, useRef} from 'react';
import { FaArrowLeft, FaRedo, FaTrophy } from 'react-icons/fa';
import styles from './RealizarTest.module.css';
import {useAuth} from '../../Auth/AuthContext'; 


const RealizarTest = ({ test, onBack }) => {
    const [preguntas, setPreguntas] = useState([]);
    const [opcionesRespuesta, setOpcionesRespuesta] = useState([]);
    const [preguntaActual, setPreguntaActual] = useState(1);
    const [respuestas, setRespuestas] = useState({});
    const [sesionId, setSesionId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [sesionInicializada, setSesionInicializada] = useState(false);    
    const [mostrandoResultados, setMostrandoResultados] = useState(false);
    const [resultados, setResultados] = useState([]);
    const [testCompletado, setTestCompletado] = useState(false);
    const [cargandoResultados, setCargandoResultados] = useState(false);
    const {getToken, userData} = useAuth(); 
    const token = getToken(); 

    const obtenerResultados = async (sesionIdParam) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/resultados/${sesionIdParam}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error('Error al obtener resultados');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener resultados:', error);
            return [];
        }
    };

    const crearORecuperarSesion = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/${test.te_clave}/sesion`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    usuarioId: userData.id
                })
            });

            if (!response.ok) {
                throw new Error('Error al crear/recuperar sesión');
            }

            const data = await response.json();
            
            if (data.sesionId) {
                setSesionId(data.sesionId);
            }

            // Verificar si existe una sesión completada
            if (data.sesionCompletada) {
                const resultadosData = await obtenerResultados(data.sesionId);
                if (resultadosData && resultadosData.length > 0) {
                    setResultados(resultadosData);
                    setTestCompletado(true);
                    setMostrandoResultados(true);
                    return; // No cargar preguntas si ya está completado
                } else {
                    console.error("No se pudieron obtener los resultados. DETALLE REAL:", error);
                    setError('No se pudieron cargar los resultados del test');
                    return;
                }
            }

            // Obtener preguntas para sesión no completada
            const preguntasResponse = await fetch(`${process.env.REACT_APP_API_URL}/test/${test.te_clave}/preguntas`, { 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!preguntasResponse.ok) throw new Error('Error al obtener las preguntas');
            const preguntasData = await preguntasResponse.json();
            const preguntasOrdenadas = preguntasData.sort((a, b) => a.pr_orden - b.pr_orden);
            setPreguntas(preguntasOrdenadas);
            
            // CORRECCIÓN PRINCIPAL: Convertir respuestas de pregunta_clave a índice de pregunta
            if (data.respuestasExistentes && typeof data.respuestasExistentes === 'object') {
                // Convertir respuestas de clave de pregunta a índice de pregunta
                const respuestasConvertidas = {};
                Object.keys(data.respuestasExistentes).forEach(preguntaClave => {
                    const indicePregunta = preguntasOrdenadas.findIndex(p => p.pr_clave === preguntaClave);
                    if (indicePregunta !== -1) {
                        respuestasConvertidas[indicePregunta + 1] = data.respuestasExistentes[preguntaClave];
                    }
                });
                
                setRespuestas(respuestasConvertidas);
            }
            
            if (data.preguntaActual !== undefined && data.preguntaActual !== null && data.preguntaActual > 0) {
                setPreguntaActual(data.preguntaActual);
            }
            
            setSesionInicializada(true);
            
        } catch (error) {
            console.error('Error al gestionar sesión:', error);
            setError('Error al inicializar la sesión del test');
            throw error;
        }
    };

    const inicializarTest = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Crear o recuperar sesión (que también carga las preguntas)
            await crearORecuperarSesion();

            if (!mostrandoResultados) {
                // Solo obtener opciones si no estamos mostrando resultados
                const opcionesResponse = await fetch(`${process.env.REACT_APP_API_URL}/test/opciones-respuestas/seleccion/${test.te_clave}`, {
                    headers: { 
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!opcionesResponse.ok) throw new Error('Error al obtener las opciones de respuesta');
                const opcionesData = await opcionesResponse.json();
                
                setOpcionesRespuesta(opcionesData);
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Error al cargar los datos del test');
        } finally {
            setLoading(false);
        }
    };

    const guardarRespuesta = async (preguntaClave, opcionValor, siguientePregunta) => {
        if (!sesionId) return;

        try {
            setGuardando(true);
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/sesion/${sesionId}/respuesta`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    preguntaClave: preguntaClave,
                    opcionValor: opcionValor,
                    preguntaActual: siguientePregunta
                })
            });

            if (!response.ok) {
                throw new Error('Error al guardar respuesta');
            }

        } catch (error) {
            console.error('Error al guardar respuesta:', error);
            setError('Error al guardar la respuesta');
        } finally {
            setGuardando(false);
        }
    };

    const seleccionarRespuesta = async (valor) => {
        if (!preguntas[preguntaActual - 1]) {
            console.error("No hay pregunta actual disponible");
            return;
        }
        const preguntaClave = preguntas[preguntaActual - 1].pr_clave;
        
        // Actualizar estado local
        setRespuestas(prev => ({
            ...prev,
            [preguntaActual]: valor
        }));

        // Calcular cuál será la siguiente pregunta
        const siguientePregunta = preguntaActual < preguntas.length ? preguntaActual + 1 : preguntaActual;
        
        // Guardar en base de datos
        await guardarRespuesta(preguntaClave, valor, siguientePregunta);

        if(preguntaActual < preguntas.length) {
            setPreguntaActual(preguntaActual + 1);
        } else {
            finalizarTest();
        }
    };

    const finalizarTest = async () => {
        if (!sesionId) return;

        try {
            // MODIFICACIÓN: Activar el estado de carga de resultados
            setCargandoResultados(true);
            setGuardando(true);
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/sesion/${sesionId}/finalizar`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al finalizar test');
            }

            const data = await response.json();
            
            // Mostrar resultados en lugar de alert
            setResultados(data.resultados || []);
            setTestCompletado(true);
            setMostrandoResultados(true);
            
        } catch (error) {
            console.error('Error al finalizar test:', error);
            setError('Error al finalizar el test');
        } finally {
            // MODIFICACIÓN: Desactivar ambos estados de carga
            setGuardando(false);
            setCargandoResultados(false);
        }
    };

    const repetirTest = async () => {
        try {
            setLoading(true);
            
            // Reiniciar estados
            setMostrandoResultados(false);
            setTestCompletado(false);
            setResultados([]);
            setRespuestas({});
            setPreguntaActual(1);
            setSesionId(null);
            setSesionInicializada(false);
            setCargandoResultados(false); // NUEVO: Reiniciar estado de carga de resultados

            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/${test.te_clave}/nueva-sesion`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    usuarioId: userData.id
                })
            });

            if (!response.ok) {
                throw new Error('Error al crear nueva sesión');
            }

            const data = await response.json();
            setSesionId(data.sesionId);
            
            // Obtener preguntas y opciones para el nuevo test
            const preguntasResponse = await fetch(`${process.env.REACT_APP_API_URL}/test/${test.te_clave}/preguntas`, { 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!preguntasResponse.ok) throw new Error('Error al obtener las preguntas');
            const preguntasData = await preguntasResponse.json();
            const preguntasOrdenadas = preguntasData.sort((a, b) => a.pr_orden - b.pr_orden);
            setPreguntas(preguntasOrdenadas);

            const opcionesResponse = await fetch(`${process.env.REACT_APP_API_URL}/test/opciones-respuestas/seleccion/${test.te_clave}`, { 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!opcionesResponse.ok) throw new Error('Error al obtener las opciones de respuesta');
            const opcionesData = await opcionesResponse.json();
            setOpcionesRespuesta(opcionesData);
            
            setSesionInicializada(true);
            
        } catch (error) {
            console.error('Error al repetir test:', error);
            setError('Error al reiniciar el test');
        } finally {
            setLoading(false);
        }
    };

    // *** CAMBIO PRINCIPAL: Función mejorada para manejar el regreso ***
    const handleVolver = () => {
        // Si el test fue completado, notificar al componente padre
        onBack(testCompletado);
    };

    const puedeAvanzar = () => {
        return respuestas[preguntaActual] !== undefined && !guardando;
    };

    const crearSesion = useRef(false);

    useEffect(() => {
        if (test && test.te_clave && !crearSesion.current) {
            crearSesion.current = true;
            inicializarTest();
        }
    }, [test]);

    const renderCargandoResultados = () => {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <h2 className={styles.loadingTitle}>¡Felicidades!</h2>
                    <p className={styles.loadingText}>
                        Has completado el test exitosamente.
                    </p>
                    <p className={styles.loadingText}>
                        Estamos calculando tus resultados...
                    </p>
                </div>
            </div>
        );
    };

    const renderResultados = () => {
    // Si resultados es null o undefined, le asignamos un arreglo vacío [] para que no truene
    const datosSeguros = resultados || []; 

    const top3Areas = datosSeguros
        .sort((a, b) => b.rt_puntaje_ponderado - a.rt_puntaje_ponderado)
        .slice(0, 3);

        return (
        <div className={styles.testResults}>
            <div className={styles.resultsHeader}>
                <h1 className={styles.resultsTitle}>
                    Resultados del Test de {test?.te_nombre}
                </h1>
            </div>
            
            <div>
                <h2 className={styles.congratsText}>¡Felicidades!</h2>
            </div>
            
            <div className={styles.resultsDescription}>
                <p>
                    Has concluido el test de {test?.te_nombre} y a continuación 
                    se muestra el área en las que destacas.
                </p>
            </div>
            
            <table className={styles.tableResult}>
                <tbody>
                {top3Areas.map((area, index) => (
                    <tr key={area.rt_area_clave} className={styles.areaItem}>
                        <td scope="row" className={styles.areaNumber}
                        >{index + 1 === 1 ? <FaTrophy className={styles.trophyOne}/> : <FaTrophy className={styles.trophyTwo}/>}</td>
                        <td>{area.ar_nombre}</td>
                        <td className={styles.areaDescriptionInline}> {Math.round(area.rt_puntaje_ponderado)}pts. - {Math.round(area.rt_porcentaje)}%/100%</td>
                        {area.ar_descripcion && (
                            <td className={styles.areaDescriptionInline}>{area.ar_descripcion}</td>
                        )}
                    </tr>
                ))}
                </tbody>
            </table>
            
            <div className={styles.resultsActions}>
                <button 
                    className={`${styles.btnRepeatTest} ${loading ? styles.btnRepeatTestDisabled : ''}`}
                    onClick={repetirTest}
                    disabled={loading}
                >
                    <FaRedo className={styles.repeatIcon} />
                    Repetir Test
                </button>
            </div>
        </div>
    );
    };

    const renderOpcionesRespuesta = () => {
        if (!opcionesRespuesta.length || !preguntas.length) return null;

        const tipoRespuesta = test.te_tipo_respuesta;
        const respuestaSeleccionada = respuestas[preguntaActual];
        const pregunta = preguntas[preguntaActual - 1];

        if (!pregunta) return null;

        if (tipoRespuesta === 'si_no') {
            return (
                <div className={`${styles.testOptions} ${styles.testOptionsSiNo}`}>
                    <p className={styles.testQuestionText}>{pregunta.pr_descripcion}</p>
                    <div className={styles.siNoButtons}>
                        {opcionesRespuesta.map(opcion => (
                            <button
                                key={opcion.op_valor}
                                className={`${styles.opcionRespuestaSiNo} ${respuestaSeleccionada === opcion.op_valor ? styles.opcionRespuestaSiNoSelected : ''}`}
                                onClick={() => seleccionarRespuesta(opcion.op_valor)}
                                data-valor={opcion.op_valor}
                                style={{
                                    '--hover-color': opcion.op_color
                                }}
                                disabled={guardando}
                            >
                                <span className={styles.opcionCirculo}></span>
                                {opcion.op_etiqueta}
                            </button>
                        ))}
                    </div>
                </div>
            );
        } else if (tipoRespuesta === 'likert') {
            return (
                <div className={`${styles.testOptions} ${styles.testOptionsLikert}`}>
                    <p className={styles.testQuestionText}>{pregunta.pr_descripcion}</p>
                    <div className={styles.likertContainer}>
                        <div className={styles.likertScale}>
                            <div className={styles.likertPoints}>
                                {opcionesRespuesta.map(opcion => (
                                    <button
                                        key={opcion.op_valor}
                                        className={`${styles.opcionRespuestaLikertPoint} ${respuestaSeleccionada === opcion.op_valor ? styles.opcionRespuestaLikertPointSelected : ''}`}
                                        onClick={() => seleccionarRespuesta(opcion.op_valor)}
                                        style={{
                                            backgroundColor: opcion.op_color
                                        }}
                                        data-tooltip={opcion.op_etiqueta}
                                        disabled={guardando}
                                    >
                                        {opcion.op_valor}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className={styles.likertLabels}>
                            <span className={styles.likertLabel}>
                                {opcionesRespuesta[0]?.op_etiqueta}
                            </span>
                            <span className={styles.likertLabel}>
                                {opcionesRespuesta[opcionesRespuesta.length - 1]?.op_etiqueta}
                            </span>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando test...</p>
                </div>
            </div>
        );
    }

    if (cargandoResultados) {
        return (
            <div className={styles.testWrapper}>
                <div className={styles.testCard}>
                    {renderCargandoResultados()}
                </div>
            </div>
        );
    }

    if (error || (!mostrandoResultados && !preguntas.length)) {
        return (
            <div className={styles.testWrapper}>
                <div className={styles.testCard}>
                    {error && <p className={styles.testError}>{error}</p>}
                    {!preguntas.length && !mostrandoResultados && <p>No se encontraron preguntas para este test.</p>}
                    {/* *** CAMBIO: Usar handleVolver en lugar de onBack directamente *** */}
                    <button className={styles.btnBackTest} onClick={handleVolver}><FaArrowLeft/>Volver</button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.testWrapper}>
                {/* *** CAMBIO: Usar handleVolver en lugar de onBack directamente *** */}
                <button className={styles.btnBackTest} onClick={handleVolver}>
                <FaArrowLeft style={{ marginRight: '8px' }}/> Volver
                </button>

            <div className={styles.testCard}>
                {mostrandoResultados ? (
                    renderResultados()
                ) : (
                    <>
                        <h1 className={styles.testTitle}>{test ? `Test de ${test.te_nombre}` : 'Test'}</h1>
                        <div className={styles.testProgress}>
                            Pregunta {preguntaActual} de {preguntas.length}
                        </div>
                        
                        {renderOpcionesRespuesta()}
                    </>
                )}
            </div>

        </div>
    );
};

export default RealizarTest;